import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, LOCALE_ID, inject, provideAppInitializer } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AudioTrack } from '../components/audio-track/audio-track.component';
import {
  Book,
  BooksManifest,
  DemoContent,
  DemosManifest,
  IntroContent,
  IntroManifest,
  SoundsManifest,
} from '../types/funnel-sample-content';
import { SpeechMarkup } from '../types/speech-markup';

const DEFAULT_LOCALE = 'en';

// Portrait pools a narrator avatar is picked from. Keeping more than one
// entry per gender lets several sample slots on the same screen show
// different faces instead of the single canned portrait every "female" or
// "male" narrator used to render.
export const FEMALE_NARRATOR_AVATARS: readonly string[] = [
  'assets/img/person3.webp',
  'assets/img/person1.png',
];
export const MALE_NARRATOR_AVATARS: readonly string[] = [
  'assets/img/person4.webp',
  'assets/img/person5.jpg',
  'assets/img/person2.png',
];

// Simple deterministic string hash (djb2) - no Math.random, so the same
// seed always maps to the same pool index across renders/refreshes.
function hashString(value: string): number {
  let hash = 5381;

  for (let index = 0; index < value.length; index++) {
    hash = (hash * 33) ^ value.charCodeAt(index);
  }

  return Math.abs(hash);
}

// Picks a narrator portrait deterministically from `seed`, skipping any
// path already in `exclude` so distinct sample slots on the same screen
// don't render the same face. Falls back to the seed's rotation slot once
// every pool entry is excluded (pool exhausted).
export function pickNarratorAvatar(
  narrator: 'male' | 'female',
  seed: string,
  exclude: ReadonlySet<string> = new Set()
): string {
  const pool = narrator === 'female' ? FEMALE_NARRATOR_AVATARS : MALE_NARRATOR_AVATARS;
  const startIndex = hashString(seed) % pool.length;

  for (let offset = 0; offset < pool.length; offset++) {
    const candidate = pool[(startIndex + offset) % pool.length];

    if (!exclude.has(candidate)) {
      return candidate;
    }
  }

  return pool[startIndex];
}

const DEMO_NARRATORS: Record<string, 'male' | 'female'> = {
  web_articles_antoni_glinda: 'male',
  web_article_laura_aaron: 'female',
  email_brandon_nikki: 'male',
  email_nikki_brandon: 'female',
  education_1: 'male',
  education_2: 'female',
  fantasy_tyler: 'male',
  fantasy_grandpa: 'female',
  selfhelp_rob: 'male',
  selfhelp_sarah: 'female',
  romance_orus: 'male',
  romance_jessica: 'female',
  religious_algieba: 'male',
  religious_umbriel: 'female',
  truecrime_jax: 'male',
  truecrime_simeon: 'female',
};
const SOUND_NARRATORS: Record<string, 'male' | 'female'> = {
  news: 'male',
  history: 'female',
  business: 'male',
  medicine: 'female',
  physics: 'male',
  darkRomance: 'female',
  environment: 'female',
  fanfiction: 'female',
  fantasy: 'male',
  finance: 'male',
  fitness: 'male',
  marketing: 'female',
  mentalHealth: 'female',
  parenting: 'female',
  personalGrowth: 'male',
  religion: 'male',
  romance: 'female',
  science: 'male',
  thrillerMystery: 'female',
  wealthBuilding: 'male',
  wellBeing: 'female',
  math: 'male',
  literature: 'female',
  cs: 'male',
  law: 'male',
  economics: 'male',
  politics: 'male',
  biology: 'female',
  arts: 'female',
};

@Injectable({
  providedIn: 'root',
})
export class FunnelSampleContentService {
  private readonly locale: string;

  private loadPromise?: Promise<void>;
  private books: Record<string, Book> = {};
  private demos: Record<string, DemoContent> = {};
  private sounds: Record<string, AudioTrack> = {};
  private intro?: IntroContent;

  constructor(
    private http: HttpClient,
    @Inject(LOCALE_ID) localeId: string
  ) {
    this.locale = this.normalizeLocale(localeId);
  }

  load(): Promise<void> {
    if (!this.loadPromise) {
      this.loadPromise = this.loadContent();
    }

    return this.loadPromise;
  }

  getCurrentLocale(): string {
    return this.locale;
  }

  getBook(id: string): Book {
    return this.requireEntry(this.books, id, 'book');
  }

  getBooks(ids: readonly string[]): Book[] {
    return ids.map(id => this.getBook(id));
  }

  /**
   * The manifest key a book was loaded under. Book entries do not carry it (their `id` is the
   * optional server id), so analytics that need a stable, locale-independent id look it up here.
   */
  getBookId(book: Book): string | undefined {
    return Object.keys(this.books).find(id => this.books[id] === book);
  }

  getDemo(id: string): DemoContent {
    return this.requireEntry(this.demos, id, 'demo');
  }

  getAudioTrack(id: string): AudioTrack {
    return this.requireEntry(this.sounds, id, 'audio sample');
  }

  getAudioTracks(ids: readonly string[]): AudioTrack[] {
    return ids.map(id => this.getAudioTrack(id));
  }

  getIntroSpeech(): IntroContent {
    if (!this.intro) {
      throw new Error('Sample intro content has not been loaded yet.');
    }

    return this.intro;
  }

  private async loadContent(): Promise<void> {
    const [books, demos, sounds, intro] = await Promise.all([
      this.loadLocalizedJson<BooksManifest>('books.json'),
      this.loadLocalizedJson<DemosManifest>('demos.json'),
      this.loadLocalizedJson<SoundsManifest>('sounds.json'),
      this.loadLocalizedJson<IntroManifest>('intro.json'),
    ]);

    const demoEntries = await Promise.all(
      Object.entries(demos).map(async ([demoId, demo]) => {
        const markups = await this.loadJson<SpeechMarkup[]>(demo.markupPath);

        return [
          demoId,
          {
            label: demo.label,
            audioPath: demo.audioPath,
            fullText: demo.fullText,
            markups,
            narrator: demo.narrator ?? DEMO_NARRATORS[demoId] ?? 'female',
          } satisfies DemoContent,
        ] as const;
      })
    );

    const introMarkups = await this.loadJson<SpeechMarkup[]>(intro.markupPath);

    this.books = books;
    this.demos = Object.fromEntries(demoEntries);
    this.sounds = Object.fromEntries(
      Object.entries(sounds).map(([soundId, sound]) => {
        const narrator = sound.narrator ?? SOUND_NARRATORS[soundId] ?? 'male';

        return [
          soundId,
          {
            ...sound,
            narrator,
            imagePath: pickNarratorAvatar(narrator, soundId),
          } satisfies AudioTrack,
        ] as const;
      })
    );
    this.intro = {
      text: intro.text,
      audioPath: intro.audioPath,
      markups: introMarkups,
      narrator: intro.narrator ?? 'female',
    };
  }

  private async loadLocalizedJson<T>(fileName: string): Promise<T> {
    const candidateLocales =
      this.locale === DEFAULT_LOCALE ? [DEFAULT_LOCALE] : [this.locale, DEFAULT_LOCALE];

    let lastError: unknown;

    for (const locale of candidateLocales) {
      try {
        return await this.loadJson<T>(this.buildLocalizedAssetPath(locale, fileName));
      } catch (error) {
        lastError = error;
      }
    }

    throw lastError;
  }

  private buildLocalizedAssetPath(locale: string, fileName: string): string {
    return `assets/sample-content/${locale}/${fileName}`;
  }

  private async loadJson<T>(assetPath: string): Promise<T> {
    return firstValueFrom(this.http.get<T>(assetPath));
  }

  private normalizeLocale(localeId: string): string {
    const [language] = localeId.toLowerCase().split('-');
    return language || DEFAULT_LOCALE;
  }

  private requireEntry<T>(collection: Record<string, T>, id: string, kind: string): T {
    const item = collection[id];

    if (!item) {
      throw new Error(`Missing ${kind} sample content for id "${id}" in locale "${this.locale}".`);
    }

    return item;
  }
}

export function provideFunnelSampleContentInitializer() {
  return provideAppInitializer(() => inject(FunnelSampleContentService).load());
}
